import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, User, Mail, Calendar, Crown, Star, Zap, Trophy } from 'lucide-react';

function getAccountAge(createdAt) {
  if (!createdAt) return 0;
  const now = new Date();
  const created = new Date(createdAt);
  return Math.floor((now - created) / (1000 * 60 * 60 * 24));
}

export default function MyAccountPage({ currentUser, onBackToHome, onShowVIPDialog }) {
  const accountAge = getAccountAge(currentUser?.created_at);

  if (!currentUser) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <p className="text-muted-foreground mb-4">Please log in to view your account</p>
            <Button onClick={onBackToHome}>Go to Home</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
      {/* Header */}
      <nav className="border-b bg-card/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <button 
              onClick={onBackToHome} 
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Back to Home</span>
            </button>
            <div className="flex items-center gap-2">
              <img 
                src="/growthyfai-logo.png" 
                alt="Growthyfai" 
                className="h-10 w-auto"
              />
              <div className="flex flex-col">
                <span className="text-lg font-bold leading-tight">PromptCraft AI</span>
                <span className="text-xs text-muted-foreground">by Growthyfai</span>
              </div>
            </div>
            <Button onClick={onBackToHome} variant="outline" size="sm">
              Home
            </Button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        
        {/* Page Header */}
        <div className="mb-8 animate-fade-in">
          <h1 className="text-4xl font-bold mb-2">My Account</h1>
          <p className="text-muted-foreground">Manage your account and view your usage statistics</p>
        </div>

        {/* Account Overview Card */}
        <Card className="mb-6 animate-slide-up">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-white text-2xl font-bold">
                  {currentUser.name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <CardTitle className="text-2xl">{currentUser.name}</CardTitle>
                  <CardDescription className="text-base">{currentUser.email}</CardDescription>
                </div>
              </div>
              <div>
                {currentUser.is_pro ? (
                  <Badge className="bg-gradient-to-r from-gold to-orange text-white px-4 py-2 text-base">
                    <Crown className="w-4 h-4 mr-2" />
                    Pro Member
                  </Badge>
                ) : (
                  <Badge variant="outline" className="px-4 py-2 text-base">
                    <Star className="w-4 h-4 mr-2" />
                    Free User
                  </Badge>
                )}
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Account Details Grid */}
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          
          {/* Personal Information */}
          <Card className="animate-slide-up" style={{ animationDelay: '0.1s' }}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5" />
                Personal Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm text-muted-foreground">Full Name</label>
                <p className="font-medium">{currentUser.name}</p>
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Email Address</label>
                <p className="font-medium">{currentUser.email}</p>
              </div>
              <div>
                <label className="text-sm text-muted-foreground">User ID</label>
                <p className="font-mono text-sm">{currentUser.id}</p>
              </div>
            </CardContent>
          </Card>

          {/* Account Statistics */}
          <Card className="animate-slide-up" style={{ animationDelay: '0.2s' }}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Trophy className="w-5 h-5" />
                Account Statistics
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm text-muted-foreground">Member Since</label>
                <p className="font-medium">
                  {currentUser.created_at 
                    ? new Date(currentUser.created_at).toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })
                    : 'N/A'}
                </p>
                <p className="text-xs text-muted-foreground mt-1">{accountAge} days ago</p>
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Last Login</label>
                <p className="font-medium">
                  {currentUser.last_login 
                    ? new Date(currentUser.last_login).toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })
                    : 'N/A'}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Usage & Plan */}
        <Card className="mb-6 animate-slide-up" style={{ animationDelay: '0.3s' }}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="w-5 h-5" />
              Usage & Plan Details
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Current Plan</label>
                {currentUser.is_pro ? (
                  <div className="flex items-center gap-2 p-4 rounded-lg bg-gradient-to-r from-gold/10 to-orange/10 border-2 border-gold/30">
                    <Crown className="w-6 h-6 text-gold" />
                    <div>
                      <p className="font-bold text-lg text-gold">Pro Member</p>
                      <p className="text-sm text-muted-foreground">Unlimited prompts & premium features</p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 p-4 rounded-lg bg-muted border-2 border-border">
                    <Star className="w-6 h-6 text-primary" />
                    <div>
                      <p className="font-bold text-lg">Free Plan</p>
                      <p className="text-sm text-muted-foreground">10 prompts per month</p>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Prompt Usage</label>
                {currentUser.is_pro ? (
                  <div className="p-4 rounded-lg bg-muted border-2 border-border">
                    <p className="text-3xl font-bold text-success">∞</p>
                    <p className="text-sm text-muted-foreground mt-1">Unlimited prompts available</p>
                  </div>
                ) : (
                  <div className="p-4 rounded-lg bg-muted border-2 border-border">
                    <div className="flex items-baseline gap-2 mb-2">
                      <span className="text-3xl font-bold">{currentUser.prompts_used || 0}</span>
                      <span className="text-muted-foreground">/ 10</span>
                    </div>
                    <div className="w-full bg-secondary rounded-full h-2 mb-2">
                      <div 
                        className="bg-primary h-2 rounded-full transition-all" 
                        style={{ width: `${((currentUser.prompts_used || 0) / 10) * 100}%` }}
                      />
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {10 - (currentUser.prompts_used || 0)} prompts remaining
                    </p>
                  </div>
                )}
              </div>
            </div>

            {!currentUser.is_pro && (
              <div className="mt-6 p-4 rounded-lg bg-primary/5 border-2 border-primary/20">
                <p className="font-medium mb-3">Want unlimited prompts?</p>
                <Button 
                  onClick={onShowVIPDialog}
                  className="w-full sm:w-auto bg-gradient-to-r from-primary to-secondary"
                >
                  <Crown className="w-4 h-4 mr-2" />
                  Upgrade to Pro
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Account Actions */}
        <Card className="animate-slide-up" style={{ animationDelay: '0.4s' }}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Quick Actions
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button 
              variant="outline" 
              className="w-full justify-start"
              onClick={onBackToHome}
            >
              <Zap className="w-4 h-4 mr-2" />
              Generate New Prompt
            </Button>
            {!currentUser.is_pro && (
              <Button 
                variant="outline" 
                className="w-full justify-start"
                onClick={onShowVIPDialog}
              >
                <Crown className="w-4 h-4 mr-2" />
                Enter VIP Access Code
              </Button>
            )}
          </CardContent>
        </Card>

      </div>
    </div>
  );
}
